<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import FunnelAPI from '../../../../api/funnel';
import agents from '../../../../api/agents';
import Modal from '../../../../components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const emit = defineEmits(['switchView']);

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const activeStageAutomationTab = ref('automations');
const agentsList = ref([]);
const saving = ref(false);
const availableItemCustomAttributeKeys = ref([]);
const stageMessageTemplates = ref([]);
const funnelsList = ref([]);

const stageAutomationTabs = [
  { id: 'automations', label: 'Automações', icon: 'settings' },
  { id: 'scheduled-follow-up', label: 'Follow Up Agendado', icon: 'calendar-clock' },
];

const selectedFunnel = computed(() => store.getters['funnel/getSelectedFunnel']);
const inboxesList = computed(() => store.getters['inboxes/getInboxes'] || []);

const stageId = computed(() => route.query.stage);
const conversationCustomAttributes = computed(() =>
  store.getters['attributes/getAttributesByModel']?.('conversation_attribute') || []
);
const contactCustomAttributes = computed(() =>
  store.getters['attributes/getAttributesByModel']?.('contact_attribute') || []
);
const conversationCustomAttributeOptions = computed(() =>
  conversationCustomAttributes.value
    .map(attr => ({
      key: attr?.attribute_key,
      name: attr?.attribute_display_name || attr?.attribute_key,
    }))
    .filter(attr => attr.key)
);
const contactCustomAttributeOptions = computed(() =>
  contactCustomAttributes.value
    .map(attr => ({
      key: attr?.attribute_key,
      name: attr?.attribute_display_name || attr?.attribute_key,
    }))
    .filter(attr => attr.key)
);

const stageMeta = computed(() => {
  const f = selectedFunnel.value;
  const sid = stageId.value;
  if (!f?.stages || !sid) return null;
  return f.stages[sid] || null;
});

const stageName = computed(() => stageMeta.value?.name || stageId.value || '');
const darkenColor = color => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darkenAmount = 0.65;
  const dr = Math.floor(r * darkenAmount);
  const dg = Math.floor(g * darkenAmount);
  const db = Math.floor(b * darkenAmount);

  return `#${dr.toString(16).padStart(2, '0')}${dg
    .toString(16)
    .padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
};
const contrastTextOnSolidHex = hex => {
  if (!hex || typeof hex !== 'string') return '#0f172a';
  const raw = hex.replace('#', '').slice(0, 6);
  if (raw.length !== 6) return '#0f172a';
  const r = parseInt(raw.substring(0, 2), 16) / 255;
  const g = parseInt(raw.substring(2, 4), 16) / 255;
  const b = parseInt(raw.substring(4, 6), 16) / 255;
  if ([r, g, b].some(c => Number.isNaN(c))) return '#0f172a';
  const lin = c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.45 ? '#0f172a' : '#ffffff';
};
const stageHeaderStyle = computed(() => {
  const color = stageMeta.value?.color;
  return color ? { backgroundColor: color } : {};
});
const stageHeaderTextColor = computed(() => {
  const color = stageMeta.value?.color;
  return color ? contrastTextOnSolidHex(color) : null;
});
const stageBackButtonStyle = computed(() => {
  const textColor = stageHeaderTextColor.value;
  if (!textColor) return {};

  const isDarkText = textColor === '#0f172a';
  return {
    color: textColor,
    '--stage-back-btn-hover-bg': isDarkText
      ? 'rgba(255, 255, 255, 0.28)'
      : 'rgba(0, 0, 0, 0.18)',
  };
});
const stageSaveButtonStyle = computed(() => {
  const color = stageMeta.value?.color;
  if (!color) return {};
  const baseColor = darkenColor(color);
  const hoverColor = color;

  return {
    '--button-bg-color': baseColor,
    '--button-text-color': contrastTextOnSolidHex(baseColor),
    '--button-hover-text-color': contrastTextOnSolidHex(hoverColor),
    '--button-hover-bg-color': hoverColor,
  };
});

const otherStages = computed(() => {
  const f = selectedFunnel.value;
  if (!f?.stages) return [];
  return Object.entries(f.stages).map(([id, s]) => ({
    id,
    name: s?.name || id,
  }));
});
const funnelOptions = computed(() =>
  (funnelsList.value || []).map(f => ({
    id: String(f.id),
    name: f.name || String(f.id),
    isCurrent: String(f.id) === String(selectedFunnel.value?.id),
  }))
);
const targetStagesByFunnel = computed(() => {
  const map = {};
  (funnelsList.value || []).forEach(funnel => {
    const stages = funnel?.stages || {};
    map[String(funnel.id)] = Object.entries(stages).map(([id, stage]) => ({
      id,
      name: stage?.name || id,
    }));
  });
  return map;
});

const rules = ref([]);
const scheduledFollowUp = ref({
  enabled: false,
  sequence: [],
});
const showScheduledFollowUpStepModal = ref(false);
const selectedScheduledFollowUpStepIndex = ref(null);
const showRemoveScheduledFollowUpStepModal = ref(false);
const scheduledFollowUpStepIndexToRemove = ref(null);
const currentStageTemplates = computed(() => stageMessageTemplates.value);
const selectedScheduledFollowUpStep = computed(() => {
  if (selectedScheduledFollowUpStepIndex.value === null) return null;

  return scheduledFollowUp.value.sequence[selectedScheduledFollowUpStepIndex.value] || null;
});

const scheduledFollowUpContactReplyOptions = [
  { id: 'any', labelKey: 'SCHEDULED_FOLLOW_UP_CONTACT_REPLY_ANY' },
  { id: 'not_replied', labelKey: 'SCHEDULED_FOLLOW_UP_CONTACT_REPLY_NOT_REPLIED' },
  { id: 'replied', labelKey: 'SCHEDULED_FOLLOW_UP_CONTACT_REPLY_REPLIED' },
];

const scheduledFollowUpTemplateName = step => {
  if (step?.message_mode === 'ai') {
    return t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_AI_LABEL');
  }

  const template = currentStageTemplates.value.find(
    candidate => String(candidate.id) === String(step?.template_id)
  );
  return template?.title || template?.id || t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_SELECT_TEMPLATE');
};

const scheduledFollowUpDelayLabel = step => {
  const parts = [];
  if (Number(step?.day) > 0) parts.push(`${Math.floor(Number(step.day))}d`);
  if (Number(step?.hours) > 0) parts.push(`${Math.floor(Number(step.hours))}h`);
  if (Number(step?.minutes) > 0) parts.push(`${Math.floor(Number(step.minutes))}min`);
  return parts.length ? parts.join(' ') : '1min';
};

const scheduledFollowUpConditionLabel = step => {
  const option = scheduledFollowUpContactReplyOptions.find(
    item => item.id === step?.conditions?.contact_reply
  );
  return t(`KANBAN.STAGE_AUTOMATIONS.${option?.labelKey || 'SCHEDULED_FOLLOW_UP_CONTACT_REPLY_ANY'}`);
};

const scheduledFollowUpVariationBadge = step => {
  const count = step?.template_variations?.length || 0;
  return `${count} ${t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_VARIATION_BADGE')}`;
};

const normalizeScheduledFollowUpConditions = rawConditions => {
  const contactReply = rawConditions?.contact_reply;
  const allowedContactReplyConditions = ['any', 'replied', 'not_replied'];
  const normalizedContactReply = allowedContactReplyConditions.includes(contactReply)
    ? contactReply
    : 'any';
  const contentMatchEnabled =
    normalizedContactReply === 'replied' &&
    rawConditions?.content_match_enabled === true;

  return {
    contact_reply: normalizedContactReply,
    content_match_enabled: contentMatchEnabled,
    content: contentMatchEnabled && rawConditions?.content
      ? String(rawConditions.content)
      : '',
  };
};

const buildEmptyScheduledFollowUpStep = () => ({
  day: 1,
  hours: 0,
  minutes: 0,
  message_mode: 'template',
  template_id: '',
  ai_instructions: '',
  template_variations: [],
  random_delay_enabled: false,
  random_delay_minutes: 0,
  conditions: normalizeScheduledFollowUpConditions(),
});

const normalizeScheduledFollowUpDelay = step => {
  const delayMinutes = Number(step?.delay_minutes);
  if (delayMinutes > 0) {
    const normalizedDelayMinutes = Math.floor(delayMinutes);
    return {
      day: Math.floor(normalizedDelayMinutes / 1440),
      hours: Math.floor((normalizedDelayMinutes % 1440) / 60),
      minutes: normalizedDelayMinutes % 60,
    };
  }

  return {
    day: Number(step?.day) > 0 ? Math.floor(Number(step.day)) : 0,
    hours: Number(step?.hours) > 0 ? Math.floor(Number(step.hours)) : 0,
    minutes: Number(step?.minutes) > 0 ? Math.floor(Number(step.minutes)) : 0,
  };
};

const normalizeScheduledFollowUpSequence = rawScheduledFollowUp => {
  const rawSequence = Array.isArray(rawScheduledFollowUp?.sequence)
    ? rawScheduledFollowUp.sequence
    : [];

  const normalizedSequence = rawSequence
    .map(step => {
      const delay = normalizeScheduledFollowUpDelay(step);
      return {
        ...delay,
        message_mode: step?.message_mode === 'ai' ? 'ai' : 'template',
        template_id: step?.template_id ? String(step.template_id) : '',
        ai_instructions: step?.ai_instructions ? String(step.ai_instructions) : '',
        template_variations: Array.isArray(step?.template_variations)
          ? step.template_variations.map(content => String(content)).filter(Boolean)
          : [],
        random_delay_enabled: Number(step?.random_delay_minutes) > 0 ||
          step?.random_delay_enabled === true,
        random_delay_minutes: Number(step?.random_delay_minutes) > 0
          ? Math.floor(Number(step.random_delay_minutes))
          : 0,
        conditions: normalizeScheduledFollowUpConditions(
          step?.conditions || rawScheduledFollowUp?.conditions
        ),
      };
    })
    .filter(step => step.message_mode === 'ai' ? step.ai_instructions : step.template_id);

  if (normalizedSequence.length > 0) {
    return normalizedSequence;
  }

  const legacyDay = Number(rawScheduledFollowUp?.days_in_stage) > 0
    ? Number(rawScheduledFollowUp?.days_in_stage)
    : 1;
  const legacyTemplateId = rawScheduledFollowUp?.template_id
    ? String(rawScheduledFollowUp.template_id)
    : '';

  return legacyTemplateId
    ? [{
        day: legacyDay,
        hours: 0,
        minutes: 0,
        message_mode: 'template',
        template_id: legacyTemplateId,
        ai_instructions: '',
        template_variations: [],
        random_delay_enabled: false,
        random_delay_minutes: 0,
        conditions: normalizeScheduledFollowUpConditions(rawScheduledFollowUp?.conditions),
      }]
    : [buildEmptyScheduledFollowUpStep()];
};

const stringOperators = [
  { id: 'equal_to', labelKey: 'OP_EQUAL_TO' },
  { id: 'contains', labelKey: 'OP_CONTAINS' },
  { id: 'not_equal_to', labelKey: 'OP_NOT_EQUAL_TO' },
];

const numericOperators = [
  { id: 'equal_to', labelKey: 'OP_EQUAL_TO' },
  { id: 'greater_than', labelKey: 'OP_GREATER_THAN' },
  { id: 'less_than', labelKey: 'OP_LESS_THAN' },
  { id: 'greater_than_or_equal', labelKey: 'OP_GREATER_THAN_OR_EQUAL' },
  { id: 'less_than_or_equal', labelKey: 'OP_LESS_THAN_OR_EQUAL' },
];

const dealStatusOptions = [
  { id: 'open', labelKey: 'STATUS_OPEN' },
  { id: 'won', labelKey: 'STATUS_WON' },
  { id: 'lost', labelKey: 'STATUS_LOST' },
];

const priorityOptions = [
  { id: 'none', labelKey: 'PRIORITY_NONE' },
  { id: 'low', labelKey: 'PRIORITY_LOW' },
  { id: 'medium', labelKey: 'PRIORITY_MEDIUM' },
  { id: 'high', labelKey: 'PRIORITY_HIGH' },
  { id: 'urgent', labelKey: 'PRIORITY_URGENT' },
];

const defaultStageAutomationActionConfig = action => {
  switch (action) {
    case 'assign_agent':
      return { agent_id: '' };
    case 'create_note':
      return { note_text: '' };
    case 'move_to_stage':
      return { stage: '' };
    case 'move_to_funnel':
      return { funnel_id: '', stage: '' };
    case 'set_item_status':
      return { status: 'open' };
    case 'set_item_priority':
      return { priority: 'none' };
    case 'set_item_value':
      return { value: '' };
    case 'set_item_custom_attribute':
      return { key: '', value: '', type: 'text' };
    case 'webhook':
      return { url: '' };
    case 'clear_assigned_agents':
    case 'complete_required_checklist':
    default:
      return {};
  }
};

const onConditionTypeChange = rule => {
  const type = rule.condition.type;
  if (type === 'item_status_equals') {
    rule.condition = { type, value: 'open' };
  } else if (type === 'item_priority_equals') {
    rule.condition = { type, value: 'none' };
  } else if (type === 'item_value_compare') {
    rule.condition = { type, operator: 'equal_to', value: '' };
  } else if (
    type === 'conversation_custom_attribute' ||
    type === 'contact_custom_attribute_equals'
  ) {
    rule.condition = { type, operator: 'equal_to', key: '', value: '' };
  } else if (type === 'item_custom_attribute_equals') {
    rule.condition = { type, key: '', value: '' };
  } else {
    rule.condition = { type, operator: 'equal_to', key: '', value: '' };
  }
};

const syncRulesFromFunnel = () => {
  const meta = stageMeta.value;
  const raw = meta?.stage_automations;
  rules.value = Array.isArray(raw) ? JSON.parse(JSON.stringify(raw)) : [];

  const rawScheduledFollowUp = meta?.scheduled_follow_up;
  scheduledFollowUp.value = {
    enabled: rawScheduledFollowUp?.enabled === true,
    sequence: normalizeScheduledFollowUpSequence(rawScheduledFollowUp),
  };
};

watch(
  () => [selectedFunnel.value?.id, selectedFunnel.value?.stages, stageId.value],
  () => syncRulesFromFunnel(),
  { immediate: true, deep: true }
);

const addStageAutomationRule = () => {
  rules.value.push({
    enabled: true,
    condition: { type: 'always', operator: 'equal_to', key: '', value: '' },
    action: 'assign_agent',
    action_config: { agent_id: '' },
  });
};

const removeStageAutomationRule = index => {
  rules.value.splice(index, 1);
};

const onStageAutomationActionChange = rule => {
  rule.action_config = defaultStageAutomationActionConfig(rule.action);
};

const opLabel = key =>
  t(`KANBAN.STAGE_AUTOMATIONS.${key}`);

const fetchAgentsList = async () => {
  try {
    const { data } = await agents.get();
    agentsList.value = data || [];
  } catch {
    agentsList.value = [];
  }
};

const loadConversationAndContactAttributes = async () => {
  try {
    await store.dispatch('attributes/get');
  } catch {
    /* atributos opcionais para condição */
  }
};

const loadItemCustomAttributeKeysForFunnel = async funnelId => {
  if (!funnelId) {
    availableItemCustomAttributeKeys.value = [];
    return;
  }

  try {
    const response = await FunnelAPI.get();
    const funnels = response.data || [];
    const currentFunnel = funnels.find(f => String(f.id) === String(funnelId));
    const globalAttrs = Array.isArray(currentFunnel?.global_custom_attributes)
      ? currentFunnel.global_custom_attributes
      : [];

    availableItemCustomAttributeKeys.value = [
      ...new Set(
        globalAttrs
          .map(attr => attr?.name)
          .filter(Boolean)
      ),
    ];
  } catch {
    availableItemCustomAttributeKeys.value = [];
  }
};

const loadFunnelsList = async () => {
  try {
    const response = await FunnelAPI.get();
    funnelsList.value = Array.isArray(response?.data) ? response.data : [];
  } catch {
    funnelsList.value = [];
  }
};

const loadStageMessageTemplates = async () => {
  const funnelId = selectedFunnel.value?.id;
  const sid = stageId.value;
  if (!funnelId || !sid) {
    stageMessageTemplates.value = [];
    return;
  }

  try {
    const response = await FunnelAPI.get();
    const funnels = response.data || [];
    const currentFunnel = funnels.find(f => String(f.id) === String(funnelId));
    const templates = currentFunnel?.stages?.[sid]?.message_templates;
    stageMessageTemplates.value = Array.isArray(templates) ? templates : [];
  } catch {
    stageMessageTemplates.value = [];
  }
};

watch(
  () => selectedFunnel.value?.id,
  async funnelId => {
    await loadItemCustomAttributeKeysForFunnel(funnelId);
  },
  { immediate: true }
);

watch(
  () => [selectedFunnel.value?.id, stageId.value],
  () => {
    loadStageMessageTemplates();
  },
  { immediate: true }
);

onMounted(async () => {
  fetchAgentsList();
  loadConversationAndContactAttributes();
  loadFunnelsList();
  loadStageMessageTemplates();
  if (!inboxesList.value.length) {
    try {
      await store.dispatch('inboxes/get');
    } catch {
      /* inboxes opcional para condição */
    }
  }
});

const goBack = () => {
  emit('switchView', 'kanban');
};

const addScheduledFollowUpStep = () => {
  scheduledFollowUp.value.sequence.push(buildEmptyScheduledFollowUpStep());
  selectedScheduledFollowUpStepIndex.value = scheduledFollowUp.value.sequence.length - 1;
  showScheduledFollowUpStepModal.value = true;
};

const removeScheduledFollowUpStep = index => {
  scheduledFollowUp.value.sequence.splice(index, 1);
  if (!scheduledFollowUp.value.sequence.length) {
    scheduledFollowUp.value.sequence.push(buildEmptyScheduledFollowUpStep());
  }
  showScheduledFollowUpStepModal.value = false;
  selectedScheduledFollowUpStepIndex.value = null;
};

const requestRemoveScheduledFollowUpStep = index => {
  scheduledFollowUpStepIndexToRemove.value = index;
  showRemoveScheduledFollowUpStepModal.value = true;
};

const closeRemoveScheduledFollowUpStepModal = () => {
  showRemoveScheduledFollowUpStepModal.value = false;
  scheduledFollowUpStepIndexToRemove.value = null;
};

const confirmRemoveScheduledFollowUpStep = () => {
  if (scheduledFollowUpStepIndexToRemove.value === null) return;
  removeScheduledFollowUpStep(scheduledFollowUpStepIndexToRemove.value);
  closeRemoveScheduledFollowUpStepModal();
};

const openScheduledFollowUpStep = index => {
  selectedScheduledFollowUpStepIndex.value = index;
  showScheduledFollowUpStepModal.value = true;
};

const closeScheduledFollowUpStep = () => {
  showScheduledFollowUpStepModal.value = false;
  selectedScheduledFollowUpStepIndex.value = null;
};

const onMoveToFunnelTargetChange = rule => {
  if (!rule?.action_config) return;
  rule.action_config.stage = '';
};

const onScheduledFollowUpConditionChange = step => {
  if (step?.conditions?.contact_reply === 'replied') return;

  step.conditions.content_match_enabled = false;
  step.conditions.content = '';
};

const selectedTemplateForStep = step => {
  return currentStageTemplates.value.find(
    template => String(template.id) === String(step?.template_id)
  );
};

const addScheduledFollowUpVariation = step => {
  const templateContent = selectedTemplateForStep(step)?.content || '';
  step.template_variations = Array.isArray(step.template_variations)
    ? step.template_variations
    : [];
  step.template_variations.push(templateContent);
};

const removeScheduledFollowUpVariation = (step, index) => {
  if (!Array.isArray(step.template_variations)) return;

  step.template_variations.splice(index, 1);
};

const normalizedScheduledFollowUpStepsForSave = () => {
  return (scheduledFollowUp.value.sequence || [])
    .map(step => {
      const day = Number(step?.day) > 0 ? Math.floor(Number(step.day)) : 0;
      const hours = Number(step?.hours) > 0 ? Math.floor(Number(step.hours)) : 0;
      const minutes = Number(step?.minutes) > 0 ? Math.floor(Number(step.minutes)) : 0;
      const delayMinutes = (day * 1440) + (hours * 60) + minutes;
      const messageMode = step?.message_mode === 'ai' ? 'ai' : 'template';

      return {
        day,
        hours,
        minutes,
        delay_minutes: delayMinutes > 0 ? delayMinutes : 1,
        message_mode: messageMode,
        template_id: messageMode === 'template' && step?.template_id ? String(step.template_id) : '',
        ai_instructions: messageMode === 'ai' && step?.ai_instructions ? String(step.ai_instructions) : '',
        template_variations: messageMode === 'template' && Array.isArray(step?.template_variations)
          ? step.template_variations.map(content => String(content)).filter(Boolean)
          : [],
        random_delay_minutes: step?.random_delay_enabled && Number(step?.random_delay_minutes) > 0
          ? Math.floor(Number(step.random_delay_minutes))
          : 0,
        conditions: normalizeScheduledFollowUpConditions(step?.conditions),
      };
    })
    .filter(step => step.message_mode === 'ai' ? step.ai_instructions : step.template_id)
    .sort((a, b) => a.delay_minutes - b.delay_minutes);
};

const save = async () => {
  const f = selectedFunnel.value;
  const sid = stageId.value;
  if (!f?.id || !sid || !stageMeta.value) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.STAGE_AUTOMATIONS.ERROR_NO_STAGE'),
      action: { type: 'error' },
    });
    return;
  }

  try {
    saving.value = true;
    const updatedStages = {
      ...f.stages,
      [sid]: {
        ...(f.stages[sid] || {}),
        stage_automations: rules.value,
        scheduled_follow_up: {
          enabled: scheduledFollowUp.value.enabled,
          sequence: normalizedScheduledFollowUpStepsForSave(),
        },
      },
    };
    await FunnelAPI.update(f.id, { stages: updatedStages });
    await store.dispatch('funnel/fetch');
    emitter.emit('newToastMessage', {
      message: t('KANBAN.STAGE_AUTOMATIONS.SAVED'),
      action: { type: 'success' },
    });
    goBack();
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.STAGE_AUTOMATIONS.ERROR_SAVE'),
      action: { type: 'error' },
    });
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-full w-full min-h-0 bg-white dark:bg-slate-900">
    <header
      class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 shrink-0"
      :style="stageHeaderStyle"
    >
      <div class="flex items-center gap-3 min-w-0">
        <button
          type="button"
          class="stage-back-button p-2 rounded-lg shrink-0"
          :style="stageBackButtonStyle"
          :title="t('KANBAN.STAGE_AUTOMATIONS.BACK')"
          @click="goBack"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <div class="min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              class="shrink-0"
              :style="{ color: stageHeaderTextColor }"
            >
              <path
                d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"
              />
            </svg>
            <h1 class="text-lg font-semibold truncate" :style="{ color: stageHeaderTextColor }">
              {{ t('KANBAN.STAGE_AUTOMATIONS.TITLE') }}
            </h1>
          </div>
          <p
            v-if="stageName"
            class="text-sm truncate"
            :style="{ color: stageHeaderTextColor, opacity: 0.85 }"
          >
            {{ stageName }}
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="solid"
        color="blue"
        size="sm"
        :is-loading="saving"
        :disabled="!stageMeta"
        :label="t('KANBAN.STAGE_AUTOMATIONS.SAVE')"
        :class="stageMeta?.color ? 'custom-colored-button' : ''"
        :style="stageSaveButtonStyle"
        @click="save"
      />
    </header>

    <div class="flex-1 overflow-y-auto p-4 md:p-6">
      <p
        v-if="!stageMeta"
        class="text-sm text-slate-500 dark:text-slate-400"
      >
        {{ t('KANBAN.STAGE_AUTOMATIONS.ERROR_NO_STAGE') }}
      </p>

      <template v-else>
        <div class="mb-4 overflow-hidden">
          <div class="flex overflow-x-auto scrollbar-hide">
            <button
              v-for="tab in stageAutomationTabs"
              :key="tab.id"
              type="button"
              class="relative flex-shrink-0 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap"
              :class="
                activeStageAutomationTab === tab.id
                  ? 'text-woot-500 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-woot-500'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100'
              "
              @click="activeStageAutomationTab = tab.id"
            >
              <span class="flex items-center gap-2">
                <fluent-icon :icon="tab.icon" size="16" />
                {{ tab.label }}
              </span>
            </button>
          </div>
        </div>

        <div
          v-if="activeStageAutomationTab === 'scheduled-follow-up'"
          class="space-y-4"
        >
          <div
            class="rounded-xl px-4 py-3"
            style="border: 1px solid #bfdbfe; background-color: #eff6ff; color: #1e3a8a;"
          >
            <p class="text-sm font-semibold">
              {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_INFO_BANNER_TITLE') }}
            </p>
            <p class="mt-1 text-sm">
              {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_INFO_BANNER_DESCRIPTION') }}
            </p>
            <p
              class="mt-2 text-xs"
              style="color: #1d4ed8;"
            >
              {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_INFO_BANNER_NOTE') }}
            </p>
          </div>

          <div class="rounded-xl border border-slate-200 dark:border-slate-700 p-4 mb-4 space-y-3 bg-white dark:bg-slate-900">
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-sm font-medium text-slate-800 dark:text-slate-100">
                {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_TITLE') }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_HELP') }}
              </p>
            </div>
            <Switch
              v-model="scheduledFollowUp.enabled"
              size="small"
            />
          </div>

          <div
            v-if="scheduledFollowUp.enabled"
            class="space-y-3"
          >
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_SEQUENCE_HELP') }}
            </p>

            <div
              v-for="(step, stepIndex) in scheduledFollowUp.sequence"
              :key="`scheduled-follow-up-step-${stepIndex}`"
              class="group flex items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/30 px-4 py-3 hover:border-woot-300 dark:hover:border-woot-700 cursor-pointer transition-colors"
              role="button"
              tabindex="0"
              @click="openScheduledFollowUpStep(stepIndex)"
              @keydown.enter.prevent="openScheduledFollowUpStep(stepIndex)"
            >
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="inline-flex items-center rounded-full bg-woot-50 dark:bg-woot-900/30 px-2 py-0.5 text-xs font-medium text-woot-700 dark:text-woot-300">
                    {{ scheduledFollowUpDelayLabel(step) }}
                  </span>
                  <span
                    v-if="step.random_delay_enabled"
                    class="inline-flex items-center rounded-full bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300"
                  >
                    +{{ step.random_delay_minutes || 0 }}min
                  </span>
                  <span
                    v-if="step.template_variations?.length"
                    class="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300"
                  >
                    {{ scheduledFollowUpVariationBadge(step) }}
                  </span>
                </div>
                <p class="mt-2 truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                  {{ scheduledFollowUpTemplateName(step) }}
                </p>
                <p class="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                  {{ scheduledFollowUpConditionLabel(step) }}
                </p>
              </div>

              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 text-ruby-600 hover:text-ruby-700 dark:text-ruby-400"
                  :title="t('KANBAN.STAGE_AUTOMATIONS.REMOVE')"
                  :aria-label="t('KANBAN.STAGE_AUTOMATIONS.REMOVE')"
                  @click.stop="requestRemoveScheduledFollowUpStep(stepIndex)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <Teleport to="body">
              <Modal
                v-model:show="showScheduledFollowUpStepModal"
                :on-close="closeScheduledFollowUpStep"
                size="medium"
              >
                <div
                  v-if="selectedScheduledFollowUpStep"
                  class="p-6 space-y-4"
                >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
                      {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_TITLE') }}
                    </h3>
                    <p class="text-xs text-slate-500 dark:text-slate-400">
                      {{ scheduledFollowUpTemplateName(selectedScheduledFollowUpStep) }}
                    </p>
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                      {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_DAYS') }}
                    </label>
                    <input
                      v-model.number="selectedScheduledFollowUpStep.day"
                      type="number"
                      min="1"
                      step="1"
                      class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                      :placeholder="t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_DAYS_PLACEHOLDER')"
                    />
                  </div>

                  <div>
                    <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                      {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_HOURS') }}
                    </label>
                    <input
                      v-model.number="selectedScheduledFollowUpStep.hours"
                      type="number"
                      min="0"
                      step="1"
                      class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                      :placeholder="t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_HOURS_PLACEHOLDER')"
                    />
                  </div>

                  <div>
                    <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                      {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_MINUTES') }}
                    </label>
                    <input
                      v-model.number="selectedScheduledFollowUpStep.minutes"
                      type="number"
                      min="0"
                      step="1"
                      class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                      :placeholder="t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_MINUTES_PLACEHOLDER')"
                    />
                  </div>
                </div>

		                <div>
		                  <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
		                    {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_MESSAGE_MODE') }}
		                  </label>
		                  <div class="relative">
		                    <select
		                      v-model="selectedScheduledFollowUpStep.message_mode"
		                      class="w-full appearance-none [background-image:none] px-3 py-2 pr-10 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
		                    >
		                      <option value="template">
		                        {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_MESSAGE_MODE_TEMPLATE') }}
		                      </option>
		                      <option value="ai">
		                        {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_MESSAGE_MODE_AI') }}
		                      </option>
		                    </select>
		                    <svg
		                      class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 dark:text-slate-400"
		                      xmlns="http://www.w3.org/2000/svg"
		                      fill="none"
		                      viewBox="0 0 24 24"
		                      stroke="currentColor"
		                      stroke-width="2"
		                      stroke-linecap="round"
		                      stroke-linejoin="round"
		                      aria-hidden="true"
		                    >
		                      <path d="m6 9 6 6 6-6" />
		                    </svg>
		                  </div>
		                </div>

	                <div v-if="selectedScheduledFollowUpStep.message_mode !== 'ai'">
	                  <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
	                    {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_TEMPLATE') }}
	                  </label>
		                  <div class="relative">
		                    <select
		                      v-model="selectedScheduledFollowUpStep.template_id"
		                      class="w-full appearance-none [background-image:none] px-3 py-2 pr-10 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
		                    >
		                      <option value="">
		                        {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_SELECT_TEMPLATE') }}
		                      </option>
		                      <option
		                        v-for="template in currentStageTemplates"
		                        :key="template.id"
		                        :value="String(template.id)"
		                      >
		                        {{ template.title || template.id }}
		                      </option>
		                    </select>
		                    <svg
		                      class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 dark:text-slate-400"
		                      xmlns="http://www.w3.org/2000/svg"
		                      fill="none"
		                      viewBox="0 0 24 24"
		                      stroke="currentColor"
		                      stroke-width="2"
		                      stroke-linecap="round"
		                      stroke-linejoin="round"
		                      aria-hidden="true"
		                    >
		                      <path d="m6 9 6 6 6-6" />
		                    </svg>
		                  </div>
		                </div>

	                <div v-if="selectedScheduledFollowUpStep.message_mode === 'ai'">
	                  <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
	                    {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_AI_INSTRUCTIONS') }}
	                  </label>
	                  <textarea
	                    v-model="selectedScheduledFollowUpStep.ai_instructions"
	                    rows="4"
	                    class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
	                    :placeholder="t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_AI_INSTRUCTIONS_PLACEHOLDER')"
	                  />
	                  <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
	                    {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_AI_HELP') }}
	                  </p>
	                </div>

	                <div v-if="selectedScheduledFollowUpStep.message_mode !== 'ai'">
                  <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_VARIATIONS') }}
                  </label>
                  <div class="space-y-2">
                    <div
                      v-for="(variation, variationIndex) in selectedScheduledFollowUpStep.template_variations"
                      :key="`scheduled-follow-up-variation-${selectedScheduledFollowUpStepIndex}-${variationIndex}`"
                      class="flex items-start gap-2"
                    >
                      <textarea
                        v-model="selectedScheduledFollowUpStep.template_variations[variationIndex]"
                        rows="3"
                        class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                        :placeholder="t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_VARIATION_PLACEHOLDER')"
                      />
                      <button
                        type="button"
                        class="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 text-ruby-600 hover:text-ruby-700 dark:text-ruby-400"
                        :title="t('KANBAN.STAGE_AUTOMATIONS.REMOVE')"
                        :aria-label="t('KANBAN.STAGE_AUTOMATIONS.REMOVE')"
                        @click="removeScheduledFollowUpVariation(selectedScheduledFollowUpStep, variationIndex)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M18 6 6 18" />
                          <path d="m6 6 12 12" />
                        </svg>
                      </button>
                    </div>
                    <Button
                      type="button"
                      color="blue"
                      size="sm"
                      :label="t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_ADD_VARIATION')"
                      :disabled="!selectedScheduledFollowUpStep.template_id"
                      @click="addScheduledFollowUpVariation(selectedScheduledFollowUpStep)"
                    />
                  </div>
                  <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_VARIATIONS_HELP') }}
                  </p>
                  <div class="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                    <span>
                      {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_AVAILABLE_VARIABLES') }}
                    </span>
                    <span
                      class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                      v-text="'{{contact.name}}'"
                    />
                    <span
                      class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                      v-text="'{{contact.first_name}}'"
                    />
                    <span
                      class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                      v-text="'{{contact.email}}'"
                    />
                    <span
                      class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                      v-text="'{{contact.phone_number}}'"
                    />
                    <span
                      class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                      v-text="'{{agent.name}}'"
                    />
                  </div>
                </div>

                <div>
                  <label class="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <input
                      v-model="selectedScheduledFollowUpStep.random_delay_enabled"
                      type="checkbox"
                      class="rounded border-slate-300 dark:border-slate-600"
                    />
                    <span>
                      {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_RANDOM_DELAY') }}
                    </span>
                  </label>
                  <input
                    v-if="selectedScheduledFollowUpStep.random_delay_enabled"
                    v-model.number="selectedScheduledFollowUpStep.random_delay_minutes"
                    type="number"
                    min="1"
                    step="1"
                    class="mt-2 w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                    :placeholder="t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_RANDOM_DELAY_PLACEHOLDER')"
                  />
                  <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_RANDOM_DELAY_HELP') }}
                  </p>
                </div>

              <div>
	                <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
	                  {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_CONTACT_REPLY_CONDITION') }}
	                </label>
	                <div class="relative">
	                  <select
	                    v-model="selectedScheduledFollowUpStep.conditions.contact_reply"
	                    class="w-full appearance-none [background-image:none] px-3 py-2 pr-10 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
	                    @change="onScheduledFollowUpConditionChange(selectedScheduledFollowUpStep)"
	                  >
	                    <option
	                      v-for="option in scheduledFollowUpContactReplyOptions"
	                      :key="option.id"
	                      :value="option.id"
	                    >
	                      {{ t(`KANBAN.STAGE_AUTOMATIONS.${option.labelKey}`) }}
	                    </option>
	                  </select>
	                  <svg
	                    class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 dark:text-slate-400"
	                    xmlns="http://www.w3.org/2000/svg"
	                    fill="none"
	                    viewBox="0 0 24 24"
	                    stroke="currentColor"
	                    stroke-width="2"
	                    stroke-linecap="round"
	                    stroke-linejoin="round"
	                    aria-hidden="true"
	                  >
	                    <path d="m6 9 6 6 6-6" />
	                  </svg>
	                </div>
                <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_CONTACT_REPLY_HELP') }}
                </p>
              </div>

              <div
                v-if="selectedScheduledFollowUpStep.conditions.contact_reply === 'replied'"
                class="space-y-2"
              >
                <label class="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input
                    v-model="selectedScheduledFollowUpStep.conditions.content_match_enabled"
                    type="checkbox"
                    class="rounded border-slate-300 dark:border-slate-600"
                  />
                  <span>
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_CONTENT_MATCH_ENABLED') }}
                  </span>
                </label>
                <input
                  v-if="selectedScheduledFollowUpStep.conditions.content_match_enabled"
                  v-model="selectedScheduledFollowUpStep.conditions.content"
                  type="text"
                  class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  :placeholder="t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_CONTENT_MATCH_PLACEHOLDER')"
                />
              </div>
                </div>
              </Modal>
            </Teleport>

            <Teleport to="body">
              <Modal
                v-model:show="showRemoveScheduledFollowUpStepModal"
                :on-close="closeRemoveScheduledFollowUpStepModal"
                size="tiny"
              >
                <div class="p-6">
                  <h3 class="text-base font-medium text-slate-900 dark:text-slate-100">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_REMOVE_TITLE') }}
                  </h3>
                  <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_REMOVE_DESCRIPTION') }}
                  </p>
                  <div class="flex justify-end gap-2 mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      color="slate"
                      size="sm"
                      :label="t('KANBAN.ACTIONS.CANCEL')"
                      @click="closeRemoveScheduledFollowUpStepModal"
                    />
                    <Button
                      type="button"
                      variant="solid"
                      color="ruby"
                      size="sm"
                      :label="t('KANBAN.ACTIONS.DELETE')"
                      @click="confirmRemoveScheduledFollowUpStep"
                    />
                  </div>
                </div>
              </Modal>
            </Teleport>

            <div class="flex justify-start">
              <Button
                type="button"
                color="blue"
                size="sm"
                :label="t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_ADD_STEP')"
                @click="addScheduledFollowUpStep"
              />
            </div>
          </div>
          </div>
        </div>

        <template v-if="activeStageAutomationTab === 'automations'">
          <div
            class="mb-4 rounded-xl px-4 py-3"
            style="border: 1px solid #bfdbfe; background-color: #eff6ff; color: #1e3a8a;"
          >
            <p class="text-sm font-semibold">
              {{ t('KANBAN.STAGE_AUTOMATIONS.INFO_BANNER_TITLE') }}
            </p>
            <p class="mt-1 text-sm">
              {{ t('KANBAN.STAGE_AUTOMATIONS.INFO_BANNER_DESCRIPTION') }}
            </p>
            <p
              class="mt-2 text-xs"
              style="color: #1d4ed8;"
            >
              {{ t('KANBAN.STAGE_AUTOMATIONS.INFO_BANNER_NOTE') }}
            </p>
          </div>

          <p class="text-sm text-slate-600 dark:text-slate-400 mb-4 w-full">
            {{ t('KANBAN.STAGE_AUTOMATIONS.HELP') }}
          </p>

          <div class="flex items-center justify-between mb-3 w-full">
            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ t('KANBAN.STAGE_AUTOMATIONS.RULES') }}
            </span>
            <Button
              type="button"
              color="blue"
              size="sm"
              :label="t('KANBAN.STAGE_AUTOMATIONS.ADD_RULE')"
              @click="addStageAutomationRule"
            />
          </div>

          <div
            v-if="rules.length === 0"
            class="empty-rules-placeholder w-full rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 px-6 py-10 flex flex-col items-center text-center bg-slate-50/80 dark:bg-slate-800/30"
          >
          <div class="empty-rules-placeholder__icon-wrapper mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
              fill="none"
              aria-hidden="true"
              class="empty-rules-placeholder__icon"
            >
              <rect
                x="8"
                y="10"
                width="32"
                height="28"
                rx="8"
                class="empty-rules-placeholder__shape"
              />
              <path
                d="M16 19h16M16 25h10M24 31h8"
                class="empty-rules-placeholder__line"
              />
              <circle
                cx="15"
                cy="31"
                r="3.5"
                class="empty-rules-placeholder__plus-bg"
              />
              <path
                d="M15 29.7v2.6M13.7 31h2.6"
                class="empty-rules-placeholder__plus"
              />
            </svg>
          </div>
          <p class="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">
            {{ t('KANBAN.STAGE_AUTOMATIONS.EMPTY') }}
          </p>
          <p class="text-sm text-slate-500 dark:text-slate-400 max-w-xl mb-4">
            {{ t('KANBAN.STAGE_AUTOMATIONS.INFO_BANNER_DESCRIPTION') }}
          </p>
          <Button
            type="button"
            color="blue"
            size="sm"
            :label="t('KANBAN.STAGE_AUTOMATIONS.ADD_RULE')"
            @click="addStageAutomationRule"
          />
        </div>

          <div
            v-else
            class="space-y-3 w-full"
          >
          <div
            v-for="(rule, ruleIndex) in rules"
            :key="ruleIndex"
            class="p-4 rounded-xl border border-slate-200 dark:border-slate-600 space-y-3 bg-slate-50/50 dark:bg-slate-800/40"
          >
            <div class="flex items-center justify-between gap-2">
              <Switch
                v-model="rule.enabled"
                size="small"
              />
              <button
                type="button"
                class="text-sm text-ruby-600 hover:text-ruby-700 dark:text-ruby-400"
                @click="removeStageAutomationRule(ruleIndex)"
              >
                {{ t('KANBAN.STAGE_AUTOMATIONS.REMOVE') }}
              </button>
            </div>

            <div class="grid grid-cols-1 gap-3">
              <div>
                <label
                  class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
                >
                  {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_TYPE') }}
                </label>
                <select
                  v-model="rule.condition.type"
                  class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  @change="onConditionTypeChange(rule)"
                >
                  <option value="always">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_ALWAYS') }}
                  </option>
                  <option value="item_custom_attribute_equals">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_ITEM_ATTR') }}
                  </option>
                  <option value="no_agent_assigned">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_NO_AGENT') }}
                  </option>
                  <option value="has_conversation">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_HAS_CONVERSATION') }}
                  </option>
                  <option value="item_status_equals">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_ITEM_STATUS') }}
                  </option>
                  <option value="item_priority_equals">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_ITEM_PRIORITY') }}
                  </option>
                  <option value="item_value_compare">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_ITEM_VALUE') }}
                  </option>
                  <option value="required_checklist_complete">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_CHECKLIST_REQUIRED') }}
                  </option>
                  <option value="contact_has_tag">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_CONTACT_TAG') }}
                  </option>
                  <option value="conversation_inbox_equals">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_CONVERSATION_INBOX') }}
                  </option>
                  <option value="conversation_custom_attribute">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_CONVERSATION_ATTR') }}
                  </option>
                  <option value="contact_custom_attribute_equals">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_CONTACT_ATTR') }}
                  </option>
                </select>
              </div>

              <template v-if="rule.condition.type === 'item_custom_attribute_equals'">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select
                    v-model="rule.condition.key"
                    class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  >
                    <option value="">
                      {{ t('KANBAN.STAGE_AUTOMATIONS.ATTR_NAME_PLACEHOLDER') }}
                    </option>
                    <option
                      v-for="attrName in availableItemCustomAttributeKeys"
                      :key="attrName"
                      :value="attrName"
                    >
                      {{ attrName }}
                    </option>
                  </select>
                  <input
                    v-model="rule.condition.value"
                    type="text"
                    class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                    :placeholder="t('KANBAN.STAGE_AUTOMATIONS.ATTR_VALUE_PLACEHOLDER')"
                  />
                </div>
              </template>

              <template v-if="rule.condition.type === 'item_status_equals'">
                <select
                  v-model="rule.condition.value"
                  class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                >
                  <option
                    v-for="opt in dealStatusOptions"
                    :key="opt.id"
                    :value="opt.id"
                  >
                    {{ t(`KANBAN.STAGE_AUTOMATIONS.${opt.labelKey}`) }}
                  </option>
                </select>
              </template>

              <template v-if="rule.condition.type === 'item_priority_equals'">
                <select
                  v-model="rule.condition.value"
                  class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                >
                  <option
                    v-for="opt in priorityOptions"
                    :key="opt.id"
                    :value="opt.id"
                  >
                    {{ t(`KANBAN.STAGE_AUTOMATIONS.${opt.labelKey}`) }}
                  </option>
                </select>
              </template>

              <template v-if="rule.condition.type === 'item_value_compare'">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select
                    v-model="rule.condition.operator"
                    class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  >
                    <option
                      v-for="op in numericOperators"
                      :key="op.id"
                      :value="op.id"
                    >
                      {{ opLabel(op.labelKey) }}
                    </option>
                  </select>
                  <input
                    v-model="rule.condition.value"
                    type="number"
                    step="any"
                    class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                    :placeholder="t('KANBAN.STAGE_AUTOMATIONS.VALUE_PLACEHOLDER')"
                  />
                </div>
              </template>

              <template v-if="rule.condition.type === 'contact_has_tag'">
                <input
                  v-model="rule.condition.value"
                  type="text"
                  class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  :placeholder="t('KANBAN.STAGE_AUTOMATIONS.TAG_PLACEHOLDER')"
                />
              </template>

              <template v-if="rule.condition.type === 'conversation_inbox_equals'">
                <select
                  v-model="rule.condition.value"
                  class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                >
                  <option value="">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SELECT_INBOX') }}
                  </option>
                  <option
                    v-for="inbox in inboxesList"
                    :key="inbox.id"
                    :value="String(inbox.id)"
                  >
                    {{ inbox.name }}
                  </option>
                </select>
              </template>

              <template
                v-if="
                  rule.condition.type === 'conversation_custom_attribute' ||
                    rule.condition.type === 'contact_custom_attribute_equals'
                "
              >
                <div class="grid grid-cols-1 gap-2">
                  <select
                    v-model="rule.condition.key"
                    class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  >
                    <option value="">
                      {{ t('KANBAN.STAGE_AUTOMATIONS.ATTR_NAME_PLACEHOLDER') }}
                    </option>
                    <option
                      v-for="attr in rule.condition.type === 'conversation_custom_attribute'
                        ? conversationCustomAttributeOptions
                        : contactCustomAttributeOptions"
                      :key="attr.key"
                      :value="attr.key"
                    >
                      {{ attr.name }}
                    </option>
                  </select>
                  <select
                    v-model="rule.condition.operator"
                    class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  >
                    <option
                      v-for="op in stringOperators"
                      :key="op.id"
                      :value="op.id"
                    >
                      {{ opLabel(op.labelKey) }}
                    </option>
                  </select>
                  <input
                    v-model="rule.condition.value"
                    type="text"
                    class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                    :placeholder="t('KANBAN.STAGE_AUTOMATIONS.ATTR_VALUE_PLACEHOLDER')"
                  />
                </div>
              </template>
            </div>

            <div>
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
              >
                {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION') }}
              </label>
              <select
                v-model="rule.action"
                class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                @change="onStageAutomationActionChange(rule)"
              >
                <option value="assign_agent">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_ASSIGN_AGENT') }}
                </option>
                <option value="clear_assigned_agents">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_CLEAR_AGENTS') }}
                </option>
                <option value="create_note">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_NOTE') }}
                </option>
                <option value="move_to_stage">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_MOVE_STAGE') }}
                </option>
                <option value="move_to_funnel">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_MOVE_FUNNEL') }}
                </option>
                <option value="set_item_status">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_SET_STATUS') }}
                </option>
                <option value="set_item_priority">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_SET_PRIORITY') }}
                </option>
                <option value="set_item_value">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_SET_VALUE') }}
                </option>
                <option value="set_item_custom_attribute">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_SET_CUSTOM_ATTR') }}
                </option>
                <option value="complete_required_checklist">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_COMPLETE_CHECKLIST') }}
                </option>
                <option value="webhook">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_WEBHOOK') }}
                </option>
              </select>
            </div>

            <div v-if="rule.action === 'assign_agent'">
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
              >
                {{ t('KANBAN.STAGE_AUTOMATIONS.AGENT') }}
              </label>
              <select
                v-model.number="rule.action_config.agent_id"
                class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
              >
                <option :value="''">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.SELECT_AGENT') }}
                </option>
                <option
                  v-for="agent in agentsList"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </div>

            <div v-if="rule.action === 'move_to_stage'">
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
              >
                {{ t('KANBAN.STAGE_AUTOMATIONS.TARGET_STAGE') }}
              </label>
              <select
                v-model="rule.action_config.stage"
                class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
              >
                <option value="">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.SELECT_STAGE') }}
                </option>
                <option
                  v-for="st in otherStages"
                  :key="st.id"
                  :value="st.id"
                >
                  {{ st.name }}
                </option>
              </select>
            </div>

            <div v-if="rule.action === 'move_to_funnel'" class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label
                  class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
                >
                  {{ t('KANBAN.STAGE_AUTOMATIONS.TARGET_FUNNEL') }}
                </label>
                <select
                  v-model="rule.action_config.funnel_id"
                  class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  @change="onMoveToFunnelTargetChange(rule)"
                >
                  <option value="">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SELECT_FUNNEL') }}
                  </option>
                  <option
                    v-for="funnel in funnelOptions"
                    :key="funnel.id"
                    :value="funnel.id"
                  >
                    {{ funnel.name }}{{ funnel.isCurrent ? ` (${t('KANBAN.STAGE_AUTOMATIONS.CURRENT_FUNNEL')})` : '' }}
                  </option>
                </select>
              </div>
              <div>
                <label
                  class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
                >
                  {{ t('KANBAN.STAGE_AUTOMATIONS.TARGET_STAGE') }}
                </label>
                <select
                  v-model="rule.action_config.stage"
                  class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  :disabled="!rule.action_config.funnel_id"
                >
                  <option value="">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SELECT_STAGE') }}
                  </option>
                  <option
                    v-for="st in targetStagesByFunnel[rule.action_config.funnel_id] || []"
                    :key="st.id"
                    :value="st.id"
                  >
                    {{ st.name }}
                  </option>
                </select>
              </div>
            </div>

            <div v-if="rule.action === 'create_note'">
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
              >
                {{ t('KANBAN.STAGE_AUTOMATIONS.NOTE_TEXT') }}
              </label>
              <textarea
                v-model="rule.action_config.note_text"
                rows="2"
                class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
              />
            </div>

            <div v-if="rule.action === 'set_item_status'">
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
              >
                {{ t('KANBAN.STAGE_AUTOMATIONS.DEAL_STATUS') }}
              </label>
              <select
                v-model="rule.action_config.status"
                class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
              >
                <option
                  v-for="opt in dealStatusOptions"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ t(`KANBAN.STAGE_AUTOMATIONS.${opt.labelKey}`) }}
                </option>
              </select>
            </div>

            <div v-if="rule.action === 'set_item_priority'">
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
              >
                {{ t('KANBAN.STAGE_AUTOMATIONS.PRIORITY') }}
              </label>
              <select
                v-model="rule.action_config.priority"
                class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
              >
                <option
                  v-for="opt in priorityOptions"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ t(`KANBAN.STAGE_AUTOMATIONS.${opt.labelKey}`) }}
                </option>
              </select>
            </div>

            <div v-if="rule.action === 'set_item_value'">
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
              >
                {{ t('KANBAN.STAGE_AUTOMATIONS.VALUE_AMOUNT') }}
              </label>
              <input
                v-model="rule.action_config.value"
                type="number"
                step="any"
                class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
              />
            </div>

            <div v-if="rule.action === 'set_item_custom_attribute'">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  v-model="rule.action_config.key"
                  type="text"
                  class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  :placeholder="t('KANBAN.STAGE_AUTOMATIONS.ATTR_NAME_PLACEHOLDER')"
                />
                <input
                  v-model="rule.action_config.value"
                  type="text"
                  class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                  :placeholder="t('KANBAN.STAGE_AUTOMATIONS.ATTR_VALUE_PLACEHOLDER')"
                />
              </div>
            </div>

            <div v-if="rule.action === 'webhook'">
              <label
                class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
              >
                {{ t('KANBAN.STAGE_AUTOMATIONS.WEBHOOK_URL') }}
              </label>
              <input
                v-model="rule.action_config.url"
                type="url"
                class="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900"
                :placeholder="t('KANBAN.STAGE_AUTOMATIONS.WEBHOOK_URL_PLACEHOLDER')"
              />
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ t('KANBAN.STAGE_AUTOMATIONS.WEBHOOK_HELP') }}
              </p>
            </div>
          </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
.custom-colored-button {
  background-color: var(--button-bg-color) !important;
  color: var(--button-text-color) !important;
  border: 1px solid var(--button-bg-color) !important;
}

.custom-colored-button:hover:not(:disabled) {
  background-color: var(--button-hover-bg-color) !important;
  border-color: var(--button-hover-bg-color) !important;
  color: var(--button-hover-text-color, var(--button-text-color)) !important;
}

.custom-colored-button:focus-visible {
  background-color: var(--button-hover-bg-color) !important;
  border-color: var(--button-hover-bg-color) !important;
  color: var(--button-hover-text-color, var(--button-text-color)) !important;
}

.stage-back-button:hover {
  background-color: var(--stage-back-btn-hover-bg, rgba(148, 163, 184, 0.25));
}

.empty-rules-placeholder__icon-wrapper {
  width: 4.25rem;
  height: 4.25rem;
  display: grid;
  place-items: center;
  border-radius: 9999px;
  background: radial-gradient(circle at 30% 30%, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.08) 60%, transparent);
}

.empty-rules-placeholder__icon {
  width: 2.8rem;
  height: 2.8rem;
}

.empty-rules-placeholder__shape {
  fill: rgba(59, 130, 246, 0.15);
  stroke: rgba(59, 130, 246, 0.45);
  stroke-width: 1.5;
}

.empty-rules-placeholder__line {
  stroke: rgba(37, 99, 235, 0.85);
  stroke-width: 2;
  stroke-linecap: round;
}

.empty-rules-placeholder__plus-bg {
  fill: rgba(37, 99, 235, 0.22);
}

.empty-rules-placeholder__plus {
  stroke: rgba(29, 78, 216, 0.95);
  stroke-width: 2;
  stroke-linecap: round;
}

.dark .empty-rules-placeholder__shape {
  fill: rgba(148, 163, 184, 0.15);
  stroke: rgba(148, 163, 184, 0.45);
}

.dark .empty-rules-placeholder__line {
  stroke: rgba(191, 219, 254, 0.9);
}

.dark .empty-rules-placeholder__plus-bg {
  fill: rgba(125, 211, 252, 0.28);
}

.dark .empty-rules-placeholder__plus {
  stroke: rgba(224, 242, 254, 0.95);
}
</style>
